import { newQuickJSWASMModuleFromVariant, QuickJSContext, QuickJSWASMModule, Scope } from "quickjs-emscripten-core";
import RELEASE_SYNC from "@jitl/quickjs-wasmfile-release-sync";

export class QuickJS {
  private static QuickJSWASM: QuickJSWASMModule;
  static context: QuickJSContext;

  /**
   * Executes the given formula with the given context. Returns the result of the execution
   * or an error, if an error occurred.
   */
  public static async compute(...args: Parameters<typeof QuickJS.computeWithDeps>) {
    const [_, result, err] = await QuickJS.computeWithDeps(...args);
    return err ? new Error(err) : result;
  }

  // this function computes a conditional that relies on information about the program state -
  // this can be passed in as info, screener, org, etc. 
  // this function returns the value of the computed formula, as well as the set of fields from 
  // the info object that were used in computing the formula (its direct dependencies).
  public static async computeWithDeps(
    formula: string,
    programState?: { [key: string]: any },
  ) : Promise<[Set<string>, any, string]> {
    const vm = await QuickJS.getQuickJSInstance();
    return Scope.withScope((scope) => {
      // create a blank _info to make sure we always at least have an _info object
      vm.setProp(vm.global, '_info', scope.manage(vm.newObject()));

      // if we have dependencies passed in, add these deps to our vm
      if (programState) {
        const keys = Object.keys(programState);
        keys.forEach((depKey: string) => {
          const depObject = programState[depKey];
          const vmDepObject = this.createVmObject(scope, vm, depObject);

          // for info, we use a Proxy in our eval to keep track of which fields 
          // in info are dependencies of the formula. So we call the info that we do pass in _info
          // in order recreate the true info as that Proxy.
          const propKey = (depKey === 'info') ? '_info' : depKey;
          vm.setProp(vm.global, propKey, vmDepObject);
        });
      }

      try {
        const code = `
          (() => {
            const deps = {};
            const info = new Proxy(_info, {
              get: (target, key) => {
                deps[key] = true;
                return _info[key];
              }
            });

            const out = ${formula};
            return [Object.keys(deps), out, ''];
          })()`;
        const out = scope.manage(vm.unwrapResult(vm.evalCode(code)));
        const toReturn = vm.dump(out);
        toReturn[0] = new Set(toReturn[0]);
        return toReturn;
      } catch (e) {
        // now error down here
        console.log(`Error evaluating with QuickJS: ${e}`);
        return [new Set<string>(), '', e instanceof Error ? `${e.message}` : (e as string)];
      }
    });
  }

  // This gives us the shared instance of the QuickJSContext that we use for all computations.
  //
  // We split out the quickjs-emscripten package partly to avoid the issues Terser was having bundling everything,
  // and partly because we don't need the full emscripten package so this saves us about 2.5MB of bundle size.
  // Official instructions are here:
  //   https://github.com/justjake/quickjs-emscripten?tab=readme-ov-file#packaging
  // 
  // The full package includes: release and debug versions of the async and synchronous variants, and we only use release+sync.
  // More info about the various options here:
  //   https://github.com/justjake/quickjs-emscripten/blob/main/doc/quickjs-emscripten-core/README.md#jitlquickjs-wasmfile-release-sync
  //
  // This code is inspired partly by their minimal example:
  //   https://github.com/justjake/quickjs-emscripten/tree/main/examples/node-minimal
  private static async getQuickJSInstance() : Promise<QuickJSContext> {
    if (!QuickJS.context || !QuickJS.QuickJSWASM) {
      QuickJS.QuickJSWASM = await newQuickJSWASMModuleFromVariant(RELEASE_SYNC)

      const vm = QuickJS.QuickJSWASM.newContext();
      const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const code = `
        originalDate = Date;
        newDate = function() {
          if (arguments.length === 1 && typeof arguments[0] === 'string') {
              const match = arguments[0].match(${dateRegex.toString()});
              if (match) {
                  const [_, month, day, year] = match;
                  return new originalDate(+year, +month - 1, +day);
              }
          }
          return new originalDate(...arguments);
        };

        newDate.now = Date.now;
        newDate.UTC = Date.UTC;
        newDate.parse = Date.parse;
    
        newDate.prototype = Date.prototype;
        Date = newDate;
        `
      const res = vm.evalCode(code);
      if (res.error) {
        res.error.dispose();
      } else {
        res.value.dispose();
      }
      QuickJS.context = vm;
    }

    return QuickJS.context;
  }

  // Helper to unpack a deeply nested object into a nested QuickJSHandle object.
  private static createVmObject(
    scope: Scope, 
    vm: QuickJSContext,
    depObject: Record<string, any> | string, 
  ) {
    if (depObject === null || depObject === undefined) {
      return scope.manage(vm.newString(''));
    }
    if (typeof depObject === 'string') {
      return scope.manage(vm.newString(depObject));
    } else if (typeof depObject === 'number') {
      return scope.manage(vm.newNumber(depObject));
    } else if (typeof depObject === 'object' && Array.isArray(depObject)) {
      const arr = scope.manage(vm.newArray());
      depObject.forEach((obj, i) => {
        const unpackedObj = this.createVmObject(scope, vm, obj);
        vm.setProp(arr, i, unpackedObj);
      }); 
      return arr;
    } else if (typeof depObject === 'boolean') {
      return scope.manage(depObject ? vm.true : vm.false);
    } else {
      // otherwise, recursively build a vm object to hold all the diff fields and values
      const vmDepObject = scope.manage(vm.newObject());
      Object.keys(depObject).forEach((key) => {
        if (depObject[key] !== null && depObject[key] !== undefined) {
          const unpackedObj = this.createVmObject(scope, vm, depObject[key]);
          vm.setProp(vmDepObject, key, unpackedObj);
        }
      });
      return vmDepObject;
    }
  }
}
